import { useFlipperFlag } from 'hooks/useFlipperFlag'
import AddReviewersStyles from '../AddReviewers/AddReviewers.module.scss'
import { Field } from '../AddReviewers/Field'
import { DueDatePicker as BaseDueDatePicker } from '../../DueDatePicker/DueDatePicker'
import { useReviewsWizard } from '../ReviewsWizardProvider'
import cx from 'classnames'
import { stopPropagation } from 'utils'
import { useSelector } from '@xstate/react'
import { EventId } from '../machineTypes'
import dayjs from 'dayjs'
import { displayEndDatePickerValue } from 'utils/informalFeedbackRequest'
import { NextOccurrence } from './NextOccurrence'
import { IntervalSelect } from './IntervalSelect'
import Styles from './ReviewersSelection.module.scss'
import { memo, useCallback, useState } from 'react'
import { DateChangedCallout } from './DateChangedCallout'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import minMax from 'dayjs/plugin/minMax'
import { AvoidConflicts, ConflictResolution } from './AvoidConflicts'
import { DueDatePickerProps } from '../../DueDatePicker/Types'
import { Select } from '../../Select/Select'
import { Item } from '@react-stately/collections'

dayjs.extend(minMax)

interface DueDateProps extends Pick<DueDatePickerProps, 'displayText' | 'variant' | 'size'> {
  fullWidth: boolean
  displayEndDate?: boolean
}

const DueDatePicker = ({
  fullWidth,
  displayText = (value) => dayjs(value as string).format('ddd, MMM D'),
  displayEndDate = true,
  variant = 'editor',
  size = 'md',
}: DueDateProps) => {
  const { service } = useReviewsWizard()
  const endDate = useSelector(service, (state) => state.context.endDate)
  const [showCallOut, setShowCallOut] = useState(false)

  const handleConflict = useCallback(
    ({ latestEnd }: Required<ConflictResolution>) => {
      service.send(EventId.SET_END_DATE, { endDate: latestEnd.format('YYYY-MM-DD') })
      setShowCallOut(true)
    },
    [service],
  )

  const children = useCallback(
    ({ latestEnd }: any) => (
      <ConditionalWrapper
        condition={showCallOut}
        wrapper={(children) => <DateChangedCallout>{children}</DateChangedCallout>}
      >
        <div className={cx(AddReviewersStyles.fieldInput, AddReviewersStyles.dueDateField)} onClick={stopPropagation}>
          <BaseDueDatePicker
            value={endDate}
            onChange={(value) => {
              setShowCallOut(false)
              service.send(EventId.SET_END_DATE, { endDate: value })
            }}
            displayText={displayText}
            minValue={dayjs().format('YYYY-MM-DD')}
            maxValue={latestEnd ? latestEnd.format('YYYY-MM-DD') : undefined}
            shape="squared"
            size={size}
            variant={variant}
            className={AddReviewersStyles.dueDatePicker}
          />
          {displayEndDate && (
            <span className={cx(AddReviewersStyles.endsOn, { [AddReviewersStyles.fullWidth]: fullWidth })}>
              {displayEndDatePickerValue(endDate)}
            </span>
          )}
        </div>
      </ConditionalWrapper>
    ),
    [endDate, service, fullWidth, showCallOut, size, variant, displayText, displayEndDate],
  )

  return <AvoidConflicts onConflict={handleConflict}>{children}</AvoidConflicts>
}

const DueDatePickerField = ({ fullWidth }: Pick<DueDateProps, 'fullWidth'>) => (
  <Field focusable={false} className={AddReviewersStyles.dueDateField}>
    <DueDatePicker fullWidth={fullWidth} />
  </Field>
)

const ScheduleField = () => {
  const isScheduledReviewsEnabled = useFlipperFlag('scheduled_reviews')
  const { canRecur } = useReviewsWizard()

  const dueDatePickerDisplay = useCallback((value: string | null) => {
    return (
      <>
        Due on <span className={Styles.label}>{dayjs(value as string).format('ddd, MMM D')}</span>
      </>
    )
  }, [])

  if (!isScheduledReviewsEnabled) {
    return (
      <>
        <span className={AddReviewersStyles.fieldLabel}>Due date</span>
        <DueDatePickerField fullWidth />
      </>
    )
  }

  return (
    <>
      <span className={AddReviewersStyles.fieldLabel}>Schedule</span>
      <div className={Styles.scheduleField}>
        <fieldset className={Styles.fieldSet}>
          <div className={Styles.field}>
            <Select
              label="Start"
              selectedKey="Now"
              renderSelectValue={(selectedItem) => (
                <span className={Styles.startSelect}>
                  Starts <span className={Styles.label}>{selectedItem.rendered}</span>
                </span>
              )}
              size="lg"
              variant="tertiary"
            >
              <Item key="Now">Now</Item>
            </Select>
          </div>
          <div className={Styles.field}>
            <DueDatePicker displayEndDate={false} fullWidth={false} displayText={dueDatePickerDisplay} size="lg" />
          </div>
          {canRecur && (
            <div className={Styles.field}>
              <IntervalSelect />
            </div>
          )}
        </fieldset>
        {canRecur && <NextOccurrence />}
      </div>
    </>
  )
}

const MemoizedScheduleField = memo(ScheduleField)

export { MemoizedScheduleField as ScheduleField }
